import { useEffect, useState } from 'react';

/** Authenticated blob fetch for image/media rendering. With a `loadAssetUrl`
 *  the object URL comes from the client.ts module cache, which OWNS the URL
 *  lifecycle — we never revoke here. Without one (tests), fall back to a plain
 *  same-origin URL. */
export interface BookmarkAssetResult {
  src: string | null;
  /** The fetch rejected — distinct from "still loading" so consumers can fall
   *  back instead of holding a loading frame. The module cache drops rejected
   *  promises, so a remount retries. */
  failed: boolean;
}

export function useBookmarkAsset(
  path: string | null,
  apiBaseUrl: string,
  loadAssetUrl?: (path: string) => Promise<string>,
): BookmarkAssetResult {
  const [state, setState] = useState<BookmarkAssetResult>({ src: null, failed: false });

  useEffect(() => {
    if (!path) {
      setState({ src: null, failed: false });
      return;
    }

    if (!loadAssetUrl) {
      setState({ src: `${apiBaseUrl}${path}`, failed: false });
      return;
    }

    let active = true;
    setState({ src: null, failed: false });

    void loadAssetUrl(path)
      .then((next) => { if (active) setState({ src: next, failed: false }); })
      .catch(() => { if (active) setState({ src: null, failed: true }); });

    return () => { active = false; };
  }, [apiBaseUrl, loadAssetUrl, path]);

  return state;
}
